import jsPDF from "jspdf";
import "jspdf-autotable";
import { Chart, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

Chart.register(...registerables, ChartDataLabels);

// Funções Utilitárias
const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(value) || 0);
const formatDate = (dateString) => new Date(dateString).toLocaleDateString('pt-BR', { timeZone: 'UTC' });

// Função para adicionar o gráfico ao PDF
const addChartToPdf = async (pdf, chartContainerRef, chartConfig, x, y, width, height) => {
    const container = chartContainerRef.current;
    if (!container) return;
    
    const canvas = document.createElement('canvas');
    canvas.width = width * 3;
    canvas.height = height * 3;
    container.appendChild(canvas);

    try {
        const chart = new Chart(canvas.getContext('2d'), { ...chartConfig, plugins: [{
            id: 'customBackgroundColor',
            beforeDraw: (chart) => {
                const { ctx } = chart;
                ctx.save();
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, chart.width, chart.height);
                ctx.restore();
            }
        }, ChartDataLabels]});
        
        await new Promise(resolve => setTimeout(resolve, 500)); 
        
        const imgData = chart.toBase64Image('image/png', 1.0);
        pdf.addImage(imgData, 'PNG', x, y, width, height, undefined, 'FAST');
        chart.destroy();

    } catch (e) { console.error("Erro ao gerar gráfico:", e); } 
    finally { container.removeChild(canvas); }
};

export const gerarRelatorioFrotaPDF = async ({ despesas, logo, chartContainerRef, tituloPersonalizado }) => {
    if (!despesas || despesas.length === 0) {
        alert("Nenhuma despesa para gerar o relatório.");
        return;
    }

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageW = pdf.internal.pageSize.getWidth();
    const margin = 15;

    const primaryColor = '#16a34a';
    const textColor = '#1e2b3b';
    const lightTextColor = '#64748b';
    const chartColors = ['#5B7A9D', '#6AB187', '#FACF5A', '#F78E69', '#A87B9A', '#77C2A7'];

    const custoTotal = despesas.reduce((sum, d) => sum + (parseFloat(d.valor) || 0), 0);
    const datas = despesas.map(d => new Date(d.data).getTime());
    const periodo = `${formatDate(new Date(Math.min(...datas)))} a ${formatDate(new Date(Math.max(...datas)))}`;
    const totaisPorTipo = despesas.reduce((acc, d) => {
        acc[d.tipo_despesa] = (acc[d.tipo_despesa] || 0) + parseFloat(d.valor);
        return acc;
    }, {});

    const titulo = tituloPersonalizado || 'Relatório de Despesas da Frota';

    // 1. Cabeçalho
    pdf.addImage(logo, 'PNG', margin, 12, 35, 14);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.setTextColor(textColor);
    pdf.text(titulo, pageW - margin, 18, { align: 'right' });
    pdf.setFontSize(9);
    pdf.setTextColor(lightTextColor);
    pdf.text(`Período: ${periodo}`, pageW - margin, 24, { align: 'right' });
    pdf.setDrawColor(primaryColor);
    pdf.setLineWidth(1);
    pdf.line(margin, 32, pageW - margin, 32);

    // 2. Gráfico de Despesas
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(textColor);
    pdf.text('Composição de Custos', margin, 45);

    const barChartConfig = {
        type: 'bar',
        data: {
            labels: Object.keys(totaisPorTipo),
            datasets: [{ data: Object.values(totaisPorTipo), backgroundColor: chartColors }],
        },
        options: {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false }, datalabels: { anchor: 'end', align: 'right', color: textColor, font: { weight: 'bold' }, formatter: (v) => `${((v/custoTotal)*100).toFixed(1)}%` } },
            scales: { x: { ticks: { callback: (v) => formatCurrency(v) } } }
        }
    };
    await addChartToPdf(pdf, chartContainerRef, barChartConfig, margin, 52, pageW - (margin * 2), 70);
    
    // 3. Tabela de Despesas
    const tableColumns = ["Data", "Veículo", "Descrição", "Fornecedor", "Total"];
    const tableRows = despesas.map(d => [
        formatDate(d.data),
        d.placa || `${d.marca} ${d.modelo}`,
        d.descricao,
        d.fornecedor_nome || 'N/A',
        formatCurrency(d.valor)
    ]);

    pdf.autoTable({
        startY: 130,
        head: [tableColumns],
        body: tableRows,
        foot: [['', '', '', 'Custo Total', formatCurrency(custoTotal)]],
        theme: 'striped',
        headStyles: { fillColor: primaryColor, textColor: 255, fontStyle: 'bold', fontSize: 9 },
        footStyles: { fillColor: [240, 240, 240], textColor: textColor, fontStyle: 'bold' },
        styles: { fontSize: 8, cellPadding: 2, textColor: textColor },
        columnStyles: {
            0: { cellWidth: 20 },
            1: { cellWidth: 35 },
            3: { cellWidth: 35 },
            4: { halign: 'right', cellWidth: 25 }
        },
        didDrawPage: (data) => {
            const pageCount = pdf.internal.getNumberOfPages();
            pdf.setFontSize(8);
            pdf.setTextColor(lightTextColor);
            pdf.text(`Página ${data.pageNumber} de ${pageCount}`, pageW / 2, pdf.internal.pageSize.height - 10, { align: 'center' });
        }
    });

    pdf.save(`relatorio-despesas-frota.pdf`);
};